import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, injectIntl } from 'react-intl';
import log from 'loglevel';
import moment from 'moment';
import classNames from 'classnames';
import { retrieveResources } from '@/shared/components/registration/resourceApi';
import ResourceAutoComplete from '@/shared/components/registration/ResourceAutoComplete';
import intlShape from '@/shared/utils/intlShape';
import OkCancelModal from '@/shared/components/OkCancelModal';
import api from '@/shared/api';
import config from '@/config';
import ToastrService from '@/shared/services/ToastrService';
import DatePicker from '@/shared/components/input/DatePicker';
import TimeInput from '@/shared/components/input/TimeInput';
import handleHttpError from '@/shared/services/HandleHttpError';
import UserSearch from '@/shared/components/input/UserSearch';
import RelationSearch from '@/shared/components/input/RelationSearch';
import AuthService from '@/shared/services/AuthService';
import InlineAttachment from '@/shared/components/InlineAttachment';
import Button from '@/shared/components/Button';
import FileUpload from '@/shared/components/input/FileUpload';
import { FolderOpenIcon, DeleteIcon } from '@/shared/components/Icons';
import DocumentSelectorModal from '@/shared/components/DocumentSelectorModal';
import { falseFunc, isBeforeMinimum } from '@/shared/utils/validationHelpers';
import { toISODate } from '@/shared/utils/dateHelper';
import { CanCreate, CanRead } from '@/shared/components/security';
import HasFeature from '@/shared/components/security/HasFeature';
import { TaskTagsMultiSelect } from '@/shared/components/input/multiselect/TaskTagsMultiSelect';
import { hasAssignments } from '@/shared/components/security/HasAssignments';
import { mapInvoiceStreamName } from '@/shared/services/ObjectDisplayService';

import { dynamicSortMultiple } from '@/shared/utils/dynamicSort';
import { getInvoiceStreamNames } from '@/shared/services/InvoiceStreamService';
import FormattedDateShort from '@/shared/components/FormattedDateShort';
import { getFirstSelectedInvoiceStreamId } from '@/shared/components/registration/registrationHelpers';
import messages from './taskMessages';

const documentUploadEndpoint = `${config.appUrls.documents}/api/documents?documentType=15&informQueues=false&recognitionStatus=4`;

class CreateToDoModal extends Component {
  state = {};

  static propTypes = {
    onSave: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
    onClose: PropTypes.func.isRequired,
    show: PropTypes.bool,
    relationId: PropTypes.string,
    documents: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        name: PropTypes.string,
      }),
    ),
    interaction: PropTypes.object,
    onSaveLinkedInteraction: PropTypes.func,
  };

  static defaultProps = {
    show: false,
    documents: null,
    relationId: null,
    interaction: null,
  };

  constructor(props) {
    super(props);
    const { documents, interaction } = props;
    this.hasAssignments = hasAssignments();

    const task = {
      taskLevel: 2, // 2 = to do
      name: '',
      remark: '',
      deadline: moment(),
      userId: undefined,
      relation: undefined,
      invoiceStreamId: undefined,
      tags: [],
      executionDuration: null,
      executionDate: null,
    };

    this.state = {
      documents: documents || [],
      task,
      showDocumentSelectorModal: false,
      invoiceStreams: [],
      interaction,
      executionDateFocused: false,
    };
  }

  componentDidMount() {
    const { intl } = this.props;
    this.form = React.createRef();

    retrieveResources(intl, 0).then(resources => {
      this.setState(({ task }) => ({
        resources,
        task: {
          ...task,
          userId: AuthService.getUserId(),
        },
      }));
    });
  }

  onResourceChanged = resource => {
    this.setState(({ task }) => ({
      task: {
        ...task,
        resourceId: resource?.id,
      },
    }));
  };

  saveToDo = () => {
    this.setState({ touchedTaskName: true, touchedRelation: true });

    const { onSave, intl, onSaveLinkedInteraction } = this.props;

    const { task, documents, interaction } = this.state;

    if (!task.name || !task.relation || !task.relation.invoiceStreamId) return;
    if (isBeforeMinimum(task.deadline)) return;

    const model = { ...task };
    model.deadline = moment.utc(model.deadline);
    model.executionDate = toISODate(model.executionDate);

    return api.base
      .post(`${config.appUrls.tasks}/api/plannedTasks`, model)
      .then(({ data: todo }) => {
        if (todo.id && interaction) {
          const interactionLink = {
            interactionId: interaction.id,
            linkId: todo.id,
            linkType: 1,
          };
          api.base.post(
            `${config.appUrls.interactions}/api/interactions/links/create`,
            interactionLink,
          );
          if (onSaveLinkedInteraction) {
            onSaveLinkedInteraction(interactionLink);
          }
        }
        if (!documents || !documents.length || !todo.id) return todo;
        const documentLinks = [];
        documents.forEach(document => {
          documentLinks.push({
            documentId: document.id,
            linkId: todo.id,
            linkType: 5,
            recognizeTemporaryDocuments: true,
          });
          // link document to relation, if present
          if (task.relationId) {
            documentLinks.push({
              documentId: document.id,
              linkId: task.relationId,
              linkType: 3,
            });
          }
          if (onSaveLinkedInteraction && interaction) {
            onSaveLinkedInteraction({
              linkId: todo.id,
              linkType: 1,
            });
          }
        });

        return api.base
          .post(`${config.appUrls.documents}/api/documents/linksmultiple`, documentLinks)
          .then(() => todo);
      })
      .then(
        data => {
          ToastrService.success(intl.formatMessage(messages.ToDoCreated));
          onSave(data);
        },
        error => {
          handleHttpError(intl)(error);
        },
      );
  };
  onDocumentUploaded = (error, document) => {
    const { intl } = this.props;

    if (error) {
      log.error(error);
      ToastrService.error(intl.formatMessage(messages.MsgEmailAttachmentUploadFailed));
      return;
    }

    const { documents } = this.state;

    if (documents.some(x => x.id === document.id)) return;

    this.setState(state => ({
      documents: [document, ...state.documents],
    }));
  };

  handleDeleteDocument = document => {
    if (document.documentType === 15) {
      // Delete if temp file
      api.base.delete(`${config.appUrls.documents}/api/documents/${document.id}`).then(() => {
        this.setState(state => ({
          documents: state.documents.filter(x => x.id !== document.id),
        }));
      });
    } else {
      this.setState(state => ({
        documents: state.documents.filter(x => x.id !== document.id),
      }));
    }
  };
  handleDocumentsSelected = selectedDocuments => {
    this.setState(({ documents }) => {
      if (!selectedDocuments || !selectedDocuments.length)
        return { showDocumentSelectorModal: false };

      const newDocuments = [...documents];
      selectedDocuments.forEach(doc => {
        if (newDocuments.some(x => x.id === doc.id)) return;
        newDocuments.unshift(doc);
      });

      return {
        showDocumentSelectorModal: false,
        documents: newDocuments,
      };
    });
  };

  closeModal = () => {
    const { onClose } = this.props;
    onClose();
  };

  deadlineChanged = deadline => {
    this.setState(({ task }) => ({
      task: {
        ...task,
        deadline,
      },
    }));
  };

  onTagsChanged = tags => {
    this.setState(({ task }) => ({
      task: {
        ...task,
        tags: tags.map(x => ({
          id: x,
        })),
      },
    }));
  };

  userChanged = user => {
    this.setState(({ task }) => ({
      task: {
        ...task,
        userId: user ? user.id : null,
      },
    }));
  };

  relationChanged = relation => {
    const { intl } = this.props;
    this.setState(({ task }) => ({
      touchedRelation: true,
      task: {
        ...task,
        // Provisional default; refined once the relation's invoice streams are fetched
        invoiceStreamId: relation ? relation.invoiceStreamId || undefined : undefined,
        relationId: relation ? relation.id : undefined,
        relation,
      },
      invoiceStreams: [],
    }));
    if (!relation?.id) return;
    getInvoiceStreamNames(relation.id).then(streams => {
      if (!streams.length) {
        const { task: currentTask } = this.state;
        // Ignore stale responses when the relation changed again in the meantime
        if (currentTask.relationId !== relation.id) return;
        ToastrService.warning(
          intl.formatMessage(messages.YouCannotSelectThisRelationBecauseItHasNoInvoiceStreams),
        );
        this.setState(({ task }) => {
          if (task.relationId !== relation.id) return null;
          return {
            task: { ...task, invoiceStreamId: undefined, relationId: undefined, relation: null },
            invoiceStreams: [],
          };
        });
        return;
      }
      this.setState(({ task }) => {
        // Ignore stale responses when the relation changed again in the meantime
        if (task.relationId !== relation.id) return null;
        return {
          task: {
            ...task,
            invoiceStreamId:
              getFirstSelectedInvoiceStreamId(streams, relation.invoiceStreamId) || undefined,
          },
          invoiceStreams: streams
            .filter(x => x.isActive)
            .map(x => ({ ...x, label: mapInvoiceStreamName(x, intl) }))
            .sort(dynamicSortMultiple('-isMain', 'label')),
        };
      });
    });
  };
  assignmentChanged = e => {
    this.setState(({ task }) => ({
      task: { ...task, invoiceStreamId: e.target.value || undefined },
    }));
  };

  taskNameChanged = name => {
    this.setState(({ task }) => ({
      touchedTaskName: true,
      task: {
        ...task,
        name,
      },
    }));
  };

  executionDurationChanged = value => {
    this.setState(({ task }) => ({
      task: {
        ...task,
        executionDuration: value,
      },
    }));
  };

  executionDateChanged = value => {
    this.setState(({ task }) => ({
      task: {
        ...task,
        executionDate: value,
      },
    }));
  };

  render() {
    const { show, intl, relationId: initialRelationId } = this.props;
    const {
      dateFocused,
      executionDateFocused,
      task,
      touchedRelation,
      touchedTaskName,
      documents,
      showDocumentSelectorModal,
      invoiceStreams,
      tags,
      resources,
      interaction,
    } = this.state;

    const taskNameClassName = classNames('form-group', {
      'has-error': touchedTaskName && !task.name,
    });

    const relationClassName = classNames('form-group', {
      'has-error': touchedRelation && (!task.relation || !task.relation.invoiceStreamId),
    });
    const initialDocumentSelectorRelation = (task && task.relation && task.relation.id) || null;
    const canCreateTags = AuthService.hasPermission({ functionality: 'Tags', privilege: 'Create' });

    return (
      <OkCancelModal
        title={intl.formatMessage(messages.CreateNewToDo)}
        show={show}
        onClose={this.closeModal}
        disabled={false}
        onSave={this.saveToDo}
      >
        {show && (
          <form ref={this.form}>
            <div className="form-horizontal form-labels-left container-fluid">
              <div className={taskNameClassName}>
                <label className="control-label col-sm-4">
                  {intl.formatMessage(messages.Description)}
                </label>
                <div className="col-sm-8">
                  <input
                    type="text"
                    autoFocus
                    value={task.name}
                    placeholder={intl.formatMessage(messages.Description)}
                    onChange={e => this.taskNameChanged(e.target.value)}
                    required
                    className="form-control"
                  />
                </div>
              </div>
              <div className={relationClassName}>
                <label className="control-label col-sm-4">
                  {intl.formatMessage(messages.Relation)}
                </label>
                <div className="col-sm-8 margin5T">
                  <RelationSearch
                    onChange={this.relationChanged}
                    changeOnInit
                    initialSelectedItem={initialRelationId ? { key: initialRelationId } : undefined}
                  />
                </div>
              </div>
              {this.hasAssignments && invoiceStreams.length > 1 && (
                <div className="form-group">
                  <label className="control-label col-sm-4">
                    <FormattedMessage {...messages.Assignment} />
                  </label>
                  <div className="col-sm-6 margin5T">
                    <select
                      className="form-control"
                      value={task.invoiceStreamId}
                      onChange={this.assignmentChanged}
                    >
                      {invoiceStreams.map(x => (
                        <option key={x.id} value={x.id}>
                          {x.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              )}

              <div
                className={classNames('form-group', {
                  'has-error': isBeforeMinimum(task.deadline),
                })}
              >
                <label className="control-label col-sm-4">
                  <FormattedMessage {...messages.Deadline} />
                </label>
                <div className="col-sm-8">
                  <DatePicker
                    placeholder={intl.formatMessage(messages.Deadline)}
                    date={task.deadline}
                    onDateChange={this.deadlineChanged}
                    focused={dateFocused}
                    onFocusChange={({ focused }) => this.setState({ dateFocused: focused })}
                    id="todoDeadline"
                    block
                    isOutsideRange={falseFunc}
                  />
                </div>
              </div>
              <HasFeature feature="CapacityPlanning">
                <div className="form-group">
                  <label className="control-label col-sm-4">
                    <FormattedMessage {...messages.ExecutionDate} />
                  </label>
                  <div className="col-sm-8">
                    <DatePicker
                      placeholder={intl.formatMessage(messages.ExecutionDate)}
                      date={task.executionDate}
                      onDateChange={this.executionDateChanged}
                      focused={executionDateFocused}
                      onFocusChange={({ focused }) =>
                        this.setState({ executionDateFocused: focused })
                      }
                      id="todoExecutionDate"
                      block
                      showClearDate
                      isOutsideRange={falseFunc}
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label className="control-label col-sm-4">
                    <FormattedMessage {...messages.ExecutionDuration} />
                  </label>
                  <div className="col-sm-8">
                    <TimeInput
                      className="form-control"
                      id="todoExecutionDuration"
                      value={task.executionDuration}
                      onChange={this.executionDurationChanged}
                      placeholder={intl.formatMessage(messages.ExecutionDuration)}
                      allowNull
                    />
                  </div>
                </div>
              </HasFeature>
              <div className="form-group">
                <label className="control-label col-sm-4">
                  <FormattedMessage {...messages.Tags} />
                </label>
                <div className="col-sm-8">
                  {task && (
                    <TaskTagsMultiSelect
                      allowNew
                      selection={task.tags.map(x => x.id)}
                      onChange={this.onTagsChanged}
                    />
                  )}
                </div>
              </div>
              <div className="form-group">
                <label className="control-label col-sm-4">
                  <FormattedMessage {...messages.AssignedTo} />
                </label>
                <div className="col-sm-8">
                  <UserSearch onChange={this.userChanged} value={task.userId} hideClearButton />
                </div>
              </div>
              <div className="form-group">
                <label className="control-label col-sm-4">
                  {intl.formatMessage(messages.PriceListItem)}
                </label>
                <div className="col-sm-8">
                  <ResourceAutoComplete
                    items={resources}
                    placeholder={intl.formatMessage(messages.PriceListItem)}
                    onChange={this.onResourceChanged}
                    emptyLabel={intl.formatMessage(messages.NoPriceListItemsFound)}
                    value={{ id: task.resourceId }}
                    crmType={task.relation?.crm}
                    required
                    intl={intl}
                    disabled={false}
                    small={false}
                  />
                </div>
              </div>
              {/* REMARK */}
              <div className="form-group">
                <label className="control-label col-sm-4">
                  <FormattedMessage {...messages.Remark} />
                </label>
                <div className="col-sm-8">
                  <textarea
                    id="RemarkField"
                    className="form-control input-sm vertical-resize"
                    value={task.remark}
                    maxLength={4000}
                    onChange={e =>
                      this.setState({
                        task: {
                          ...task,
                          remark: e.target.value,
                        },
                      })
                    }
                  />
                </div>
              </div>
              {/* Linked documents */}

              <CanRead on="Documents">
                <div className="form-group">
                  <label className="control-label col-sm-4">
                    <FormattedMessage {...messages.Documents} />
                  </label>
                  <div className="col-sm-8">
                    {documents && documents.length > 0 && (
                      <div className="d1">
                        {documents.map(doc => (
                          <InlineAttachment
                            key={doc.id}
                            attachment={doc}
                            onDelete={this.handleDeleteDocument}
                          />
                        ))}
                      </div>
                    )}
                    <CanCreate on="Documents">
                      <div className="file-upload-wrapper">
                        <FileUpload
                          endpoint={documentUploadEndpoint}
                          onFileUploaded={(error, file) => this.onDocumentUploaded(error, file)}
                          removeSuccessfullAfter={5000}
                        />
                      </div>
                      <Button
                        btnClassName="btn tbn-default btn-block margin10T"
                        onClick={() => this.setState({ showDocumentSelectorModal: true })}
                      >
                        <FolderOpenIcon className="margin5R" />
                        <FormattedMessage {...messages.ChooseDocumentInAdminPulse} />
                      </Button>
                    </CanCreate>
                  </div>
                </div>
              </CanRead>

              {interaction && (
                <div className="form-group">
                  <label className="control-label col-sm-4">
                    <FormattedMessage {...messages.Interaction} />
                  </label>
                  <div className="control-label col-sm-8 interaction-link">
                    <span>
                      {interaction.title} - <FormattedDateShort value={interaction.date} />
                    </span>
                    <DeleteIcon
                      className="delete-icon"
                      onClick={() =>
                        this.setState({
                          interaction: null,
                        })
                      }
                      tooltip={intl.formatMessage(messages.DeleteinteractionLink)}
                    />
                  </div>
                </div>
              )}
            </div>
          </form>
        )}
        {show && showDocumentSelectorModal && (
          <DocumentSelectorModal
            show
            initialRelation={initialDocumentSelectorRelation}
            onDocumentsSelected={this.handleDocumentsSelected}
            onClose={() => this.setState({ showDocumentSelectorModal: false })}
          />
        )}
      </OkCancelModal>
    );
  }
}

export default injectIntl(CreateToDoModal);
